import React from "react";
import {
  ChartPie,
  Book,
  Users,
  PenLine,
  MessageCircle,
  LifeBuoy,
  FileText,
  Receipt,
  ClipboardList,
  Users as UsersIcon,
  Menu,
  ClipboardCheck,
  Handshake,
} from "lucide-react";
import { NavLink, Outlet, Navigate, Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useLogoutUserMutation } from "@/features/api/authApi";
import { toast } from "sonner";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuGroup,
} from "@radix-ui/react-dropdown-menu";
import logo from "@/assets/image/logo/fetc-logo.png";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Main Sidebar Component
const Sidebar = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [logoutUser, { data, isSuccess }] = useLogoutUserMutation();
  const navigate = useNavigate();

  // Redirect to login if not authenticated or not admin/instructor
  if (!isAuthenticated || (user?.role !== "admin" && user?.role !== "instructor")) {
    return <Navigate to="/login" replace />;
  }

  // Logout handler
  const logoutHandler = async () => {
    try {
      await logoutUser().unwrap();
      toast.success(data?.message || "Logged out successfully");
      navigate("/");
    } catch (error) {
      toast.error(error?.data?.message || "Error logging out");
    }
  };

  // NavLink styling
  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-2 p-2 rounded-md transition-colors hover:bg-gray-200 dark:hover:bg-gray-700 ${
      isActive ? "bg-gray-300 dark:bg-gray-600 font-semibold" : ""
    }`;

  const isAdmin = user?.role === "admin";
  const isInstructor = user?.role === "instructor";

  // Main Menu Component
  const MainMenu = ({ isMobile = false }) => (
    <nav className={isMobile ? "space-y-4" : "space-y-2"}>
      {isAdmin && (
        <NavLink to="/admin/dashboard" className={navLinkClass}>
          <ChartPie size={22} className="text-gray-600 dark:text-gray-300" />
          <span className="text-gray-800 dark:text-gray-200">Dashboard</span>
        </NavLink>
      )}

      {isAdmin && (
        <>
          <NavLink to="/admin/courses" className={navLinkClass}>
            <Book size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">Courses</span>
          </NavLink>

          <NavLink to="/admin/users" className={navLinkClass}>
            <Users size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">Users</span>
          </NavLink>

          <NavLink to="/admin/pages" className={navLinkClass}>
            <FileText size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">Pages</span>
          </NavLink>

          <NavLink to="/admin/posts" className={navLinkClass}>
            <PenLine size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">Posts</span>
          </NavLink>

           <NavLink to="/admin/mock-tests" className={navLinkClass}>
            <ClipboardCheck size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">Mock Test</span>
          </NavLink>

        </>
      )}

      {(isAdmin || isInstructor) && (
        <>
          <NavLink to="/admin/newsflash" className={navLinkClass}>
            <MessageCircle size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">News Flash</span>
          </NavLink>

          <NavLink to="/admin/support/list" className={navLinkClass}>
            <LifeBuoy size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">Support Tickets</span>
          </NavLink>

          <NavLink to="/admin/invoices" className={navLinkClass}>
            <Receipt size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">Invoice</span>
          </NavLink>

          <NavLink to="/admin/leads" className={navLinkClass}>
            <ClipboardList size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">Leads Dashboard</span>
          </NavLink>

          <NavLink to="/admin/partner-list" className={navLinkClass}>
            <Handshake size={22} className="text-gray-600 dark:text-gray-300" />
            <span className="text-gray-800 dark:text-gray-200">Partner List</span>
          </NavLink>
        </>
      )}
    </nav>
  );

  // Avatar Dropdown Menu
  const AvatarMenu = ({ user, logoutHandler }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="p-0 h-10 w-10 rounded-full">
          <Avatar className="h-10 w-10">
            <AvatarImage src={user?.photoUrl || "/image/avatar.jpg"} alt="Profile" />
            <AvatarFallback className="bg-gray-200 dark:bg-gray-700">
              {user?.name?.charAt(0) || "A"}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-56 mt-2 rounded-md bg-white border border-gray-200 shadow-md dark:bg-gray-900 dark:border-gray-700 p-2">
        <DropdownMenuGroup>
          <DropdownMenuItem className="focus:outline-none">
            <Link
              to="/dashboard/profile"
              className="block w-full px-3 py-2 rounded-md text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Profile
            </Link>
          </DropdownMenuItem>

          {user?.role === "admin" && (
            <DropdownMenuItem className="focus:outline-none">
              <Link
                to="/admin/dashboard"
                className="block w-full px-3 py-2 rounded-md text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Master Panel
              </Link>
            </DropdownMenuItem>
          )}

          {user?.role === "instructor" && (
            <DropdownMenuItem className="focus:outline-none">
              <Link
                to="/admin/newsflash"
                className="block w-full px-3 py-2 rounded-md text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                CRM Panel
              </Link>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>

        <DropdownMenuSeparator className="h-px bg-gray-200 dark:bg-gray-700 my-2" />

        <DropdownMenuItem
          onClick={logoutHandler}
          className="text-red-600 px-3 py-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900 cursor-pointer transition-colors focus:outline-none"
        >
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
<div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Navbar */}
      <div className="h-20 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-4 md:px-8 shadow-md">
       <Link to="/" className="flex-shrink-0 relative z-[60]">
  <img
    src="/image/logo/fetc-logo.png"
    alt="FETC Logo"
    className="h-12 md:h-16 w-auto object-contain cursor-pointer"
  />
</Link>
        <div className="flex items-center gap-4">
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[250px] bg-gray-100 dark:bg-gray-800">
                <SheetHeader>
                  <SheetTitle className="text-xl font-bold text-gray-800 dark:text-gray-200">
                    {isAdmin ? "Admin Panel" : "Instructor Panel"}
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <MainMenu isMobile />
                </div>
              </SheetContent>
            </Sheet>
          </div>
          <div className="hidden lg:block">
            <AvatarMenu user={user} logoutHandler={logoutHandler} />
          </div>
          <div className="lg:hidden">
            <AvatarMenu user={user} logoutHandler={logoutHandler} />
          </div>
        </div>
      </div>

      {/* Main Content */}
<div className="flex flex-1 min-h-0">
        {/* Sidebar */}
<div className="w-[250px] border-r border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 p-5 overflow-y-auto hidden lg:block">
          <h1 className="text-xl font-bold mb-6 border-b pb-2 text-gray-800 dark:text-gray-200">
            {isAdmin ? "Admin Panel" : "Instructor Panel"}
          </h1>
          <MainMenu />
        </div>

        {/* Content Area */}
<div className="flex-1 bg-white dark:bg-gray-900 overflow-y-auto min-h-0">
  <div className="md:p-10 p-2">
    <Outlet />
  </div>
</div>
      </div>
    </div>
  );
};

export default Sidebar;